import { css } from '@emotion/react';
import { TextButton, fonts, mq, space } from 'folio-common-components';
import {
  formatters,
  isIbanLike,
  makeFikenDeepLink,
  parseOrgNum,
} from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import type { EventItemFragment } from '../../common-client-queries.generated';
import { Download2Icon, SmallNewViewFor200Icon } from '../../icons';
import { pages } from '../../pages';
import { Bookkeeping } from '../../routes/payment/payment-details/bookkeeping';
import { OverlayMenu } from '../OverlayMenu';
import { DefData, DefList, DefTitle } from '../definition-list';
import { OrgLink, useOrgPathAbsoluteUrl } from '../org-navigation';
import {
  getCustomLedgerTitle,
  taxAdministrationAccountNumber,
} from '../payment-helpers';
import {
  CompleteLabel,
  ReservedLabel,
  getEventCompletionStatus,
  getOverriddenByItegrationName,
} from '../shared/bookkeeping';
import { Divider, EventContentWrapper, StyledMenuLink } from './shared';

interface BookkeepingProps {
  event: EventItemFragment;
  editable: boolean;
  isInternalTransfer: boolean;
}

interface Props extends BookkeepingProps {
  isSingleTransaction: boolean;
  backlink?: string;
}

const EventContent: React.FC<Props> = ({
  event,
  isSingleTransaction,
  backlink,
  ...props
}) => {
  const {
    dateTime,
    movementType,
    transaction,
    payment,
    merchant,
    card,
    physicalTerminal,
    amount,
  } = event;

  const isTransferIn = movementType === 'Receive';
  const isTransferOut = movementType === 'Send';

  const debtorAccountNumber =
    payment?.debtorAccount || transaction?.debtorAccountNumber;
  const creditorAccountNumber =
    payment?.creditorAccount || transaction?.creditorAccountNumber;

  const outgoingCardTransaction = card && amount && Number(amount) < 0;

  return (
    <EventContentWrapper>
      <EventContentBookkeeping
        event={event}
        isTransferIn={isTransferIn}
        {...props}
      />

      {isSingleTransaction ? (
        <>
          <Divider />
          <div>
            <DefList
              css={css`
                display: grid;
                ${space([32], 'row-gap')};
                ${space([48], 'column-gap')};

                @media ${mq.large} {
                  grid-template-columns: 1fr 1fr;
                }
              `}
            >
              <div>
                <DefTitle>
                  {outgoingCardTransaction ? 'Tidspunkt for kjøp' : 'Tidspunkt'}
                </DefTitle>
                <DefData>
                  <time dateTime={dateTime}>
                    {formatters.capitalize(
                      formatters.formatDate(
                        dateTime,
                        'eeee do MMMM yyyy, HH:mm',
                      ),
                    )}
                  </time>
                </DefData>
              </div>

              {transaction?.bookingDate && card ? (
                <div>
                  <DefTitle>Trukket fra konto</DefTitle>
                  <DefData>
                    <time dateTime={transaction.bookingDate}>
                      {formatters.capitalize(
                        formatters.formatDate(
                          transaction.bookingDate,
                          'eeee do MMMM yyyy',
                        ),
                      )}
                    </time>
                  </DefData>
                </div>
              ) : null}

              {isTransferIn && debtorAccountNumber ? (
                <div>
                  <DefTitle>Avsenders kontonummer</DefTitle>
                  <DefData>
                    {formatters.formatAccountNumber(debtorAccountNumber)}
                  </DefData>
                </div>
              ) : null}

              {isTransferOut && creditorAccountNumber ? (
                <div>
                  <DefTitle>Mottakers kontonummer</DefTitle>
                  <DefData>
                    {isIbanLike(creditorAccountNumber)
                      ? formatters.formatIban(creditorAccountNumber)
                      : formatters.formatAccountNumber(creditorAccountNumber)}
                    {creditorAccountNumber === taxAdministrationAccountNumber
                      ? ' – Skatteetaten'
                      : ''}
                  </DefData>
                </div>
              ) : null}

              {(isTransferOut && payment?.kid) || transaction?.kid ? (
                <div>
                  <DefTitle>KID</DefTitle>
                  <DefData>{payment?.kid || transaction?.kid}</DefData>
                </div>
              ) : null}

              {isTransferOut && payment?.message ? (
                <EventMessage message={payment.message} />
              ) : null}

              {isTransferIn && transaction?.remittanceInformation ? (
                <EventMessage message={transaction.remittanceInformation} />
              ) : null}

              {isTransferOut && merchant ? (
                <div>
                  <DefTitle>Mottaker</DefTitle>
                  <DefData>
                    {merchant.name}
                    {card
                      ? physicalTerminal
                        ? ' (kortterminal)'
                        : ' (kjøp på nett)'
                      : null}
                  </DefData>
                </div>
              ) : null}

              {payment?.accountingSystemInformation ? (
                <div>
                  <DefTitle>Sendt fra</DefTitle>
                  <DefData>
                    {payment.accountingSystemInformation.systemName}
                  </DefData>
                </div>
              ) : payment?.createdBy ? (
                <div>
                  <DefTitle>Opprettet av</DefTitle>
                  <DefData>{payment.createdBy.structuredName.full}</DefData>
                </div>
              ) : null}

              {payment?.signedBy ? (
                <div>
                  <DefTitle>Godkjent av</DefTitle>
                  <DefData>{payment.signedBy.structuredName.full}</DefData>
                </div>
              ) : null}
            </DefList>
          </div>
        </>
      ) : null}

      <TransactionFooter
        event={event}
        isSingleTransaction={isSingleTransaction}
        backlink={backlink}
        isInternalTransfer={props.isInternalTransfer}
        isTransferOut={isTransferOut}
      />
    </EventContentWrapper>
  );
};

const EventMessage: React.FC<{ message: string }> = ({ message }) => {
  message = message.trim();
  return (
    <div
      css={css`
        overflow-x: auto;
      `}
    >
      <DefTitle>Melding</DefTitle>
      {/* If the message includes a line-break, assume it should be pre-formatted */}
      {message.includes('\n') ? (
        <DefData
          css={css`
            ${fonts.font100book};
            font-family: MaisonNeueMono, monospace;
            white-space: pre;
          `}
        >
          {message}
        </DefData>
      ) : (
        <DefData>{message}</DefData>
      )}
    </div>
  );
};

const AccountingSystemFooter: React.FC<{ name: string }> = ({ name }) => {
  const orgNum = parseOrgNum(window.location.href);

  // Need to add integration links here as they onboard the API
  if (name !== 'Fiken') {
    return null;
  }

  return (
    <div>
      <a
        href={makeFikenDeepLink('kjopsoversikt', orgNum)}
        target="_blank"
        css={css`
          color: inherit;
          display: inline-flex;
        `}
      >
        Åpne i {name}
        <SmallNewViewFor200Icon
          css={css`
            ${space([8], 'margin-left')};
            margin-top: 1px;
          `}
        />
      </a>
    </div>
  );
};

const TransactionFooter: React.FC<{
  event: EventItemFragment;
  isSingleTransaction?: boolean;
  backlink?: string;
  isInternalTransfer: boolean;
  isTransferOut: boolean;
}> = ({
  event,
  isSingleTransaction,
  backlink,
  isInternalTransfer,
  isTransferOut,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const { fid, payment, transaction, card } = event;
  const urlWithOrgPath = useOrgPathAbsoluteUrl();
  const voucherUrl = urlWithOrgPath(`api/export/voucher/${fid}`);
  const confirmationUrl = urlWithOrgPath(`betalingsbekreftelse/${fid}`);
  const externalIntegrationOverride = getOverriddenByItegrationName(event);
  const accountingSystemInformation = payment?.accountingSystemInformation;
  const accountingSystemName =
    accountingSystemInformation?.systemName || externalIntegrationOverride;
  const reserved = transaction === null && card !== null;
  const status = getEventCompletionStatus(event);
  const showConfirmationLink =
    transaction && !isInternalTransfer && isTransferOut;
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuItems = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        ${space([16], 'gap')};
      `}
    >
      <StyledMenuLink href={voucherUrl} download={true}>
        <Download2Icon css={space([8], 'margin-right')} />
        Bilag til regnskap
      </StyledMenuLink>
      <StyledMenuLink
        href={showConfirmationLink ? confirmationUrl : undefined}
        download={true}
        css={css`
          color: ${showConfirmationLink ? 'inherit' : colors.grayShadow};
          pointer-events: ${showConfirmationLink ? null : 'none'};
        `}
      >
        <Download2Icon css={space([8], 'margin-right')} />
        Betalingsbekreftelse
      </StyledMenuLink>
    </div>
  );

  return (
    <>
      <Divider />

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: end; /* fallback */
          align-items: last baseline;
          ${space([8], 'gap')};
        `}
      >
        {accountingSystemName ? (
          <AccountingSystemFooter name={accountingSystemName} />
        ) : (
          <div
            css={css`
              display: grid;
              ${space([8], 'gap')};
              @media ${mq.medium} {
                grid-template-columns: auto 1fr;
              }
            `}
          >
            {reserved && <ReservedLabel />}
            <CompleteLabel
              data-testid="event-completion-progress"
              status={status}
            />
          </div>
        )}
        {!isSingleTransaction ? (
          <OrgLink
            to={pages.singleTransaction.getUrl(fid)}
            css={css`
              color: inherit;
            `}
            backlink={backlink}
          >
            Detaljer
          </OrgLink>
        ) : transaction ? (
          <div
            css={css`
              position: relative;
            `}
          >
            <TextButton
              onClick={() => setShowModal(!showModal)}
              ref={buttonRef}
              icon={<Download2Icon />}
            >
              Last ned PDF
            </TextButton>
            <OverlayMenu
              isOpen={showModal}
              openButton={buttonRef}
              position="right"
              onClose={() => setShowModal(!showModal)}
              showCloseButton={false}
              css={css`
                top: auto;
                ${space([16], 'padding')};
                width: auto !important;
                @media not all and (min-width: 500px) {
                  /* Hack to make sure the menu stays inside the viewport. We should
                         have a proper way to do this. */
                  left: -110px;
                }
              `}
            >
              {menuItems}
            </OverlayMenu>
          </div>
        ) : (
          'Venter på info'
        )}
      </div>
    </>
  );
};

const EventContentBookkeeping: React.FC<
  BookkeepingProps & { isTransferIn: boolean }
> = ({ event, editable, isTransferIn, isInternalTransfer }) => {
  if (isInternalTransfer) {
    return null;
  }

  const { payment, ledgerCategoryInfo } = event;

  const showBookkeeping = payment?.external
    ? getCustomLedgerTitle(ledgerCategoryInfo) == null
    : true;

  const overrideIntegrationName = getOverriddenByItegrationName(event);

  if (!showBookkeeping) {
    return null;
  }

  return (
    <>
      <Divider />

      <div>
        <Bookkeeping
          accountingSystem={
            overrideIntegrationName
              ? {
                  name: overrideIntegrationName,
                  ledgerAccount:
                    payment?.accountingSystemInformation?.ledgerAccount ?? null,
                }
              : undefined
          }
          event={event}
          categorySuggestionFids={ledgerCategoryInfo.guesses.map(
            guess => guess.fid,
          )}
          hideCompleteLabel={true}
          hideRequirements={Boolean(payment?.accountingSystemInformation)}
          categorySelectorLabel="Kategori til regnskapet"
          defaultCategorySelectorText={
            isTransferIn ? 'Hvorfor fikk du penger?' : 'Hva betalte du for?'
          }
          editable={editable && !overrideIntegrationName}
        />
      </div>
    </>
  );
};

// React.lazy only supports default exports:
// https://reactjs.org/docs/code-splitting.html#named-exports
// eslint-disable-next-line import/no-default-export
export default EventContent;
